import type { DropdownData, Value } from '@fitogest/reactive-form-builder/interfaces';
import { setDropdownSelected, setDropdownSelectedFromArray } from '@fitogest/reactive-form-builder/utils';
import { ReactiveCatalogSource } from '@fitogest/reactive-form-builder/catalog';
import type { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import type { ParcelasEnvironmentDefinition } from '../environment-variables';
import {
    PARCELAS_SIEX_CATALOG_SOURCE,
    type ParcelasDropdownDataSourceFactory,
    type SourceAwareParcelasCatalogRow
} from './parcelas-dropdown-datasource.factory';

export type ParcelasCatalogHydrationContext = {
    readonly previousData: ParcelasModel;
    readonly env: ParcelasEnvironmentDefinition;
    readonly formType: number;
    readonly cultivosDropdown: DropdownData<unknown>;
    readonly variedadesDropdown: DropdownData<unknown>;
    readonly sectoresDropdown?: DropdownData<unknown>;
    readonly sistemasRiegoDropdown?: DropdownData<unknown>;
    readonly usuariosDropdown?: DropdownData<unknown>;
    readonly aplicadoresDropdown?: DropdownData<unknown>;
    readonly comunidadesRegantesDropdown?: DropdownData<unknown>;
    readonly origenesAguaRiegoDropdown?: DropdownData<unknown>;
    readonly dataSourceFactory: ParcelasDropdownDataSourceFactory;
};

/**
 * Hydrates legacy Parcelas dropdown values into temporary display options.
 *
 * Legacy persisted rows can contain plain SIEX/local ids while the declarative dropdowns now work with
 * canonical values such as `siex:122` or `propio:77:3`. This adapter keeps that compatibility layer out of
 * the visual component and also seeds temporary dropdown options so edit forms can display saved values before
 * the network catalog finishes loading. It also seeds dependent dropdowns such as sector and irrigation system
 * because their catalogs can load after the edit/duplicate value has already been patched into the form.
 */
export class ParcelasCatalogHydrator {

    public static transformPreviousData(context: ParcelasCatalogHydrationContext): ParcelasModel {
        const transformedData = { ...context.previousData } as Record<string, unknown>;

        this.seedPreviousPrimitiveSelections(context, transformedData);

        if (context.env.activateApiSIEX) {
            const currentCultivo = transformedData['id_cultivo'];
            const currentVariedad = transformedData['id_variedad'];
            const parsedCultivo = context.dataSourceFactory.parseCatalogKey(currentCultivo);
            const source = parsedCultivo.source ?? PARCELAS_SIEX_CATALOG_SOURCE;
            const cultivoCode = context.dataSourceFactory.resolveUsableCatalogCode(
                parsedCultivo.primaryCode,
                currentCultivo,
                transformedData['codigo_cultivo'],
                transformedData['id_cultivo'],
            );
            const cultivoLabel = this.resolveDisplayLabel(
                context,
                [cultivoCode, parsedCultivo.primaryCode, currentCultivo],
                transformedData['cultivo_nombre'],
                transformedData['nombre_cultivo'],
                transformedData['cultivo'],
            );
            const cultivoKeyPart = context.dataSourceFactory.resolveFirstCatalogValue(cultivoCode, cultivoLabel);

            if (cultivoKeyPart !== '' && !parsedCultivo.source) {
                transformedData['id_cultivo'] = context.dataSourceFactory.buildCatalogKey(source, cultivoKeyPart);
            }

            const parsedVariedad = context.dataSourceFactory.parseCatalogKey(currentVariedad);
            const variedadCode = context.dataSourceFactory.resolveUsableCatalogCode(
                parsedVariedad.secondaryCode,
                currentVariedad,
                transformedData['id_variedad'],
                transformedData['codigo_variedad'],
            );
            const variedadLabel = this.resolveDisplayLabel(
                context,
                [variedadCode, parsedVariedad.secondaryCode, currentVariedad],
                transformedData['variedad_nombre'],
                transformedData['nombre_variedad'],
                this.resolveZeroVariedadLabel(variedadCode),
                transformedData['variedad'],
            );
            const variedadKeyPart = context.dataSourceFactory.resolveFirstCatalogValue(variedadCode, variedadLabel);

            if (cultivoKeyPart !== '' && variedadKeyPart !== '' && !parsedVariedad.source) {
                transformedData['id_variedad'] = context.dataSourceFactory.buildCatalogKey(source, cultivoKeyPart, variedadKeyPart);
            }
        }

        this.seedPreviousCatalogSelections(
            context,
            transformedData,
            transformedData['id_cultivo'],
            transformedData['id_variedad'],
        );

        return transformedData as ParcelasModel;
    }

    private static seedPreviousCatalogSelections(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
        cultivoValue: unknown,
        variedadValue: unknown,
    ): void {
        const cultivoKey = context.dataSourceFactory.toStringId(cultivoValue);

        if (cultivoKey !== '') {
            const cultivoRaw = this.buildPreviousCultivoOptionRaw(context, previousData, cultivoKey);
            const cultivoOption = context.dataSourceFactory.mapCultivoDropdownOption(cultivoRaw);

            this.markDropdownDataAsHydrationSeed(context.cultivosDropdown);
            ReactiveCatalogSource.upsertDropdownOption(context.cultivosDropdown, cultivoOption);
            setDropdownSelected(context.cultivosDropdown, cultivoRaw);
        }

        const variedadKey = context.dataSourceFactory.toStringId(variedadValue);

        if (variedadKey !== '') {
            const variedadRaw = this.buildPreviousVariedadOptionRaw(context, previousData, cultivoKey, variedadKey);
            const variedadOption = context.dataSourceFactory.mapVariedadDropdownOption(variedadRaw);

            this.markDropdownDataAsHydrationSeed(context.variedadesDropdown);
            ReactiveCatalogSource.upsertDropdownOption(context.variedadesDropdown, variedadOption);
            setDropdownSelected(context.variedadesDropdown, variedadRaw);
        }
    }

    private static seedPreviousPrimitiveSelections(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        this.seedPreviousSectorSelection(context, previousData);
        this.seedPreviousSistemaRiegoSelection(context, previousData);
        this.seedPreviousUsuarioSelection(context, previousData);
        this.seedPreviousAplicadoresSelection(context, previousData);
        this.seedPreviousComunidadRegantesSelection(context, previousData);
        this.seedPreviousOrigenAguaRiegoSelection(context, previousData);
    }

    private static seedPreviousSectorSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const id = context.dataSourceFactory.resolveFirstCatalogValue(previousData['id_sector'], previousData['sector']);

        if (id === '' || !context.sectoresDropdown) {
            return;
        }

        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['sector'],
            previousData['sector_nombre'],
            previousData['nombre_sector'],
            id,
        );
        const raw = {
            ...previousData,
            id,
            id_sector: id,
            id_finca: context.dataSourceFactory.resolveFirstCatalogValue(previousData['id_finca'], previousData['finca']),
            nombre: label,
        };

        this.markDropdownDataAsHydrationSeed(context.sectoresDropdown);
        ReactiveCatalogSource.upsertDropdownOption(context.sectoresDropdown, {
            label,
            value: id,
            raw,
            valueAliases: this.buildPrimitiveAliases(
                id,
                previousData['sector'],
                previousData['sector_nombre'],
                previousData['nombre_sector'],
            ),
        });
        setDropdownSelected(context.sectoresDropdown, raw);
    }

    private static seedPreviousSistemaRiegoSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const codigo = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['codigo_sistema_riego'],
            previousData['codigo_SIEX_sistema_riego'],
            previousData['sistema_riego'],
        );

        if (codigo === '' || !context.sistemasRiegoDropdown) {
            return;
        }

        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['sistema_riego'],
            previousData['sistema_de_riego'],
            previousData['nombre_sistema_riego'],
            codigo,
        );
        const raw = {
            ...previousData,
            codigo_SIEX: codigo,
            sistema_de_riego: label,
        };

        this.markDropdownDataAsHydrationSeed(context.sistemasRiegoDropdown);
        ReactiveCatalogSource.upsertDropdownOption(context.sistemasRiegoDropdown, {
            label,
            value: codigo,
            raw,
            valueAliases: this.buildPrimitiveAliases(
                codigo,
                previousData['sistema_riego'],
                previousData['sistema_de_riego'],
                previousData['nombre_sistema_riego'],
            ),
        });
        setDropdownSelected(context.sistemasRiegoDropdown, raw);
    }

    private static seedPreviousUsuarioSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const id = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['id_responsable_asignado'],
            previousData['responsable_nombre'],
            previousData['grabador'],
        );

        if (id === '' || !context.usuariosDropdown) {
            return;
        }

        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['responsable_nombre'],
            previousData['grabador_nombre'],
            previousData['grabador'],
            id,
        );
        const raw = {
            ...previousData,
            id,
            nombre: label,
            responsable_nombre: label,
        };

        this.seedSinglePrimitiveOption(
            context.usuariosDropdown,
            label,
            id,
            raw,
            previousData['id_responsable_asignado'],
            previousData['responsable_nombre'],
            previousData['grabador_nombre'],
            previousData['grabador'],
        );
    }

    private static seedPreviousAplicadoresSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const id = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['aplicadores'],
            previousData['id_aplicador'],
            previousData['id_asesor'],
            previousData['asesor'],
        );

        if (id === '' || !context.aplicadoresDropdown) {
            return;
        }

        const label = this.resolveDisplayLabel(
            context,
            [id, previousData['aplicadores'], previousData['id_aplicador'], previousData['id_asesor'], previousData['asesor']],
            this.resolveExistingDropdownLabel(
                context.aplicadoresDropdown,
                id,
                previousData['aplicadores'],
                previousData['id_aplicador'],
                previousData['id_asesor'],
                previousData['asesor'],
            ),
            previousData['aplicadores_nombre'],
            previousData['asesor_nombre'],
            previousData['asesor'],
            previousData['aplicadores'],
            id,
        );
        const raw = {
            ...previousData,
            id,
            nombre: label,
        };
        const option = this.buildPrimitiveOption(
            label,
            id,
            raw,
            previousData['aplicadores'],
            previousData['aplicadores_nombre'],
            previousData['id_aplicador'],
            previousData['id_asesor'],
            previousData['asesor_nombre'],
            previousData['asesor'],
        );

        this.markDropdownDataAsHydrationSeed(context.aplicadoresDropdown);
        ReactiveCatalogSource.upsertDropdownOption(context.aplicadoresDropdown, option);

        if (context.env.appName === 'sequiador') {
            setDropdownSelectedFromArray(context.aplicadoresDropdown, [raw], true);
            return;
        }

        setDropdownSelected(context.aplicadoresDropdown, raw);
    }

    private static seedPreviousComunidadRegantesSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const id = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['id_comunidad_regantes'],
            previousData['comunidad_regantes'],
            previousData['comunidad_regantes_nombre'],
        );

        if (id === '' || !context.comunidadesRegantesDropdown) {
            return;
        }

        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['comunidad_regantes_nombre'],
            previousData['comunidad_regantes'],
            id,
        );
        const raw = {
            ...previousData,
            id,
            nombre: label,
        };

        this.seedSinglePrimitiveOption(
            context.comunidadesRegantesDropdown,
            label,
            id,
            raw,
            previousData['id_comunidad_regantes'],
            previousData['comunidad_regantes'],
            previousData['comunidad_regantes_nombre'],
        );
    }

    private static seedPreviousOrigenAguaRiegoSelection(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
    ): void {
        const codigo = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['codigo_procedencia_agua'],
            previousData['codigo_SIEX_procedencia_agua'],
            previousData['procedencia_agua'],
            previousData['procedencia_del_agua_de_riego'],
        );

        if (codigo === '' || !context.origenesAguaRiegoDropdown) {
            return;
        }

        const label = context.dataSourceFactory.resolveFirstCatalogValue(
            previousData['procedencia_agua'],
            previousData['procedencia_del_agua_de_riego'],
            previousData['origen_agua_riego'],
            codigo,
        );
        const raw = {
            ...previousData,
            codigo_SIEX: codigo,
            procedencia_del_agua_de_riego: label,
        };

        this.seedSinglePrimitiveOption(
            context.origenesAguaRiegoDropdown,
            label,
            codigo,
            raw,
            previousData['codigo_procedencia_agua'],
            previousData['codigo_SIEX_procedencia_agua'],
            previousData['procedencia_agua'],
            previousData['procedencia_del_agua_de_riego'],
            previousData['origen_agua_riego'],
        );
    }


    private static resolveZeroVariedadLabel(variedadCode: string): string {
        return variedadCode === '0' ? 'SIN VARIEDAD' : '';
    }

    private static resolveDisplayLabel(
        context: ParcelasCatalogHydrationContext,
        identityValues: readonly unknown[],
        ...labelCandidates: readonly unknown[]
    ): string {
        const identityTokens = new Set(
            identityValues
                .map((value) => this.normalizeComparableToken(context.dataSourceFactory.toStringId(value)))
                .filter((value) => value !== ''),
        );
        let fallback = '';

        for (const candidate of labelCandidates) {
            const label = context.dataSourceFactory.toStringId(candidate);

            if (label === '') {
                continue;
            }

            if (fallback === '') {
                fallback = label;
            }

            if (!identityTokens.has(this.normalizeComparableToken(label))) {
                return label;
            }
        }

        return fallback;
    }

    private static resolveExistingDropdownLabel(
        dropdown: DropdownData<unknown> | undefined,
        ...identityValues: readonly unknown[]
    ): string {
        if (!dropdown) {
            return '';
        }

        const identityTokens = new Set(
            identityValues
                .map((value) => this.normalizeComparableToken(ReactiveCatalogSource.toStringId(value)))
                .filter((value) => value !== ''),
        );

        if (identityTokens.size === 0) {
            return '';
        }

        for (const option of this.getDropdownOptions(dropdown)) {
            const optionTokens = this.getOptionIdentityCandidates(option)
                .map((value) => this.normalizeComparableToken(ReactiveCatalogSource.toStringId(value)))
                .filter((value) => value !== '');

            if (optionTokens.some((value) => identityTokens.has(value))) {
                return ReactiveCatalogSource.toStringId(option.label);
            }
        }

        return '';
    }

    private static getDropdownOptions(dropdown: DropdownData<unknown>): Array<Value<unknown>> {
        const options = [...(dropdown.filtered ?? []), ...(dropdown.values ?? [])] as Array<Value<unknown>>;
        const seenValues = new Set<string>();
        const uniqueOptions: Array<Value<unknown>> = [];

        for (const option of options) {
            const key = ReactiveCatalogSource.toStringId(option.value) + '|' + ReactiveCatalogSource.toStringId(option.label);

            if (seenValues.has(key)) {
                continue;
            }

            seenValues.add(key);
            uniqueOptions.push(option);
        }

        return uniqueOptions;
    }

    private static getOptionIdentityCandidates(option: Value<unknown>): readonly unknown[] {
        const raw = option.raw !== null && typeof option.raw === 'object'
            ? option.raw as Record<string, unknown>
            : {};

        return [
            option.value,
            ...(option.valueAliases ?? []),
            raw['catalogKey'],
            raw['catalogCode'],
            raw['id'],
            raw['codigo'],
            raw['codigo_cultivo'],
            raw['codigo_variedad'],
            raw['codigo_SIEX'],
            raw['id_cultivo'],
            raw['id_variedad'],
            raw['id_aplicador'],
            raw['id_asesor'],
            raw['aplicadores'],
            raw['asesor'],
            raw['nombre'],
            raw['cultivo'],
            raw['variedad'],
        ];
    }

    private static normalizeComparableToken(value: string): string {
        const normalizedValue = value
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .replace(/\s+/g, ' ')
            .toLowerCase();

        if (/^0+\d+$/.test(normalizedValue)) {
            return String(Number.parseInt(normalizedValue, 10));
        }

        return normalizedValue;
    }

    private static seedSinglePrimitiveOption(
        dropdown: DropdownData<unknown>,
        label: string,
        value: string,
        raw: Record<string, unknown>,
        ...aliases: readonly unknown[]
    ): void {
        this.markDropdownDataAsHydrationSeed(dropdown);
        ReactiveCatalogSource.upsertDropdownOption(dropdown, this.buildPrimitiveOption(label, value, raw, ...aliases));
        setDropdownSelected(dropdown, raw);
    }

    private static buildPrimitiveOption(
        label: string,
        value: string,
        raw: Record<string, unknown>,
        ...aliases: readonly unknown[]
    ): Value<unknown> {
        return {
            label,
            value,
            raw,
            valueAliases: this.buildPrimitiveAliases(value, ...aliases),
        };
    }

    private static buildPrimitiveAliases(...values: readonly unknown[]): string[] {
        return values
            .map((value) => ReactiveCatalogSource.toStringId(value))
            .filter((value, index, aliases) => value !== '' && aliases.indexOf(value) === index);
    }

    private static markDropdownDataAsHydrationSeed(data: DropdownData<unknown>): void {
        data.provenance = 'hydrationSeed';
    }

    private static buildPreviousCultivoOptionRaw(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
        catalogKey: string,
    ): SourceAwareParcelasCatalogRow {
        const parsedKey = context.dataSourceFactory.parseCatalogKey(catalogKey);
        const source = parsedKey.source ?? PARCELAS_SIEX_CATALOG_SOURCE;
        const catalogCode = context.dataSourceFactory.resolveUsableCatalogCode(
            parsedKey.primaryCode,
            previousData['codigo_cultivo'],
            previousData['id_cultivo'],
        );
        const cultivo = this.resolveDisplayLabel(
            context,
            [catalogKey, catalogCode, previousData['id_cultivo']],
            this.resolveExistingDropdownLabel(context.cultivosDropdown, catalogKey, catalogCode, previousData['id_cultivo'], previousData['cultivo']),
            previousData['cultivo_nombre'],
            previousData['nombre_cultivo'],
            previousData['cultivo'],
            catalogCode,
        );

        return {
            ...previousData,
            id: catalogCode,
            id_cultivo: catalogCode,
            codigo: catalogCode,
            codigo_cultivo: catalogCode,
            cultivo,
            source,
            catalogKey,
            catalogCode,
        } as unknown as SourceAwareParcelasCatalogRow;
    }

    private static buildPreviousVariedadOptionRaw(
        context: ParcelasCatalogHydrationContext,
        previousData: Record<string, unknown>,
        cultivoKey: string,
        catalogKey: string,
    ): SourceAwareParcelasCatalogRow {
        const parsedCultivo = context.dataSourceFactory.parseCatalogKey(cultivoKey);
        const parsedVariedad = context.dataSourceFactory.parseCatalogKey(catalogKey);
        const source = parsedVariedad.source ?? parsedCultivo.source ?? PARCELAS_SIEX_CATALOG_SOURCE;
        const cultivoCode = context.dataSourceFactory.resolveUsableCatalogCode(
            parsedVariedad.primaryCode,
            parsedCultivo.primaryCode,
            previousData['codigo_cultivo'],
            previousData['id_cultivo'],
        );
        const variedadCode = context.dataSourceFactory.resolveUsableCatalogCode(
            parsedVariedad.secondaryCode,
            previousData['id_variedad'],
            previousData['codigo_variedad'],
        );
        const cultivo = this.resolveDisplayLabel(
            context,
            [cultivoKey, cultivoCode, previousData['id_cultivo']],
            this.resolveExistingDropdownLabel(context.cultivosDropdown, cultivoKey, cultivoCode, previousData['id_cultivo'], previousData['cultivo']),
            previousData['cultivo_nombre'],
            previousData['nombre_cultivo'],
            previousData['cultivo'],
            cultivoCode,
        );
        const variedad = this.resolveDisplayLabel(
            context,
            [catalogKey, variedadCode, previousData['id_variedad']],
            this.resolveExistingDropdownLabel(context.variedadesDropdown, catalogKey, variedadCode, previousData['id_variedad'], previousData['variedad']),
            previousData['variedad_nombre'],
            previousData['nombre_variedad'],
            this.resolveZeroVariedadLabel(variedadCode),
            previousData['variedad'],
            variedadCode,
        );

        return {
            ...previousData,
            id: variedadCode,
            id_cultivo: cultivoCode,
            id_variedad: variedadCode,
            codigo: cultivoCode,
            codigo_cultivo: cultivoCode,
            codigo_variedad: variedadCode,
            cultivo,
            variedad,
            source,
            catalogKey,
            catalogCode: variedadCode,
        } as unknown as SourceAwareParcelasCatalogRow;
    }
}
